import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, ChevronDown, ChevronUp, Minus } from 'lucide-react';

const FULL_WIDTH = 384; // px, matches w-96
const FULL_MIN_HEIGHT = 200; // px, approximate min content height used for bounds
const ICON_SIZE = 56; // px, approx button size

const ValidationErrorSidebar = ({ 
  errors, 
  validationErrors,
  isVisible, 
  onFieldClick,
  title = "Validation Errors"
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [position, setPosition] = useState({ x: window.innerWidth - 400, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const sidebarRef = useRef(null);
  const didDragRef = useRef(false);

  const clampToViewport = useCallback((x, y, width, height) => {
    const maxX = Math.max(0, window.innerWidth - width);
    const maxY = Math.max(0, window.innerHeight - height);
    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY))
    };
  }, []);

  // Mouse event handlers for dragging (minimized and expanded)
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    didDragRef.current = false;
    const rect = sidebarRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    const width = isMinimized ? ICON_SIZE : FULL_WIDTH;
    const height = isMinimized ? ICON_SIZE : FULL_MIN_HEIGHT;

    const clamped = clampToViewport(newX, newY, width, height);
    setPosition(clamped);

    didDragRef.current = true;
  }, [isDragging, dragOffset, isMinimized, clampToViewport]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners
  useEffect(() => {
    if (!isDragging) return;

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleFieldClick = (fieldKey) => {
    if (onFieldClick) onFieldClick(fieldKey);
  };

  const handleCollapseClick = () => {
    if (didDragRef.current) return; // Ignore click after dragging
    setIsCollapsed(!isCollapsed);
  };

  const handleMinimizeClick = () => {
    if (didDragRef.current) return; // Ignore click after dragging
    setIsMinimized(!isMinimized);
  };

  // When toggling minimized/expanded, adjust position so the panel/icon remains inside viewport
  useEffect(() => {
    const width = isMinimized ? ICON_SIZE : FULL_WIDTH;
    const height = isMinimized ? ICON_SIZE : FULL_MIN_HEIGHT;
    setPosition((prev) => clampToViewport(prev.x, prev.y, width, height));
  }, [isMinimized, clampToViewport]);

  // Only show if visible
  if (!isVisible) return null;

  // Use validationErrors if errors is not provided, and ensure it's not null/undefined
  const errorObject = errors || validationErrors || {};
  const errorCount = Object.keys(errorObject).length;
  
  // Debug logging
  console.log('ValidationErrorSidebar props:', { errors, validationErrors, isVisible, title });
  console.log('ValidationErrorSidebar errorObject:', errorObject);
  console.log('ValidationErrorSidebar errorCount:', errorCount);

  // Minimized state always takes precedence
  if (isMinimized) {
    return (
      <motion.div
        initial={{ x: 0, y: 0 }}
        animate={{ x: 0, y: 0 }}
        exit={{ x: 0, y: 0 }}
        className="fixed z-[9999] cursor-move select-none"
        style={{ left: position.x, top: position.y }}
        ref={sidebarRef}
        onMouseDown={handleMouseDown}
      >
        <button onClick={handleMinimizeClick} className={`${errorCount === 0 ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} text-white p-3 rounded-full shadow-lg transition-colors`} title="Show Error Details">
          <AlertCircle className="w-5 h-5" />
          <span className="ml-2 text-sm font-medium">{errorCount}</span>
        </button>
      </motion.div>
    );
  }

  // No errors state
  if (errorCount === 0) {
  return (
      <motion.div
        ref={sidebarRef}
        initial={{ x: 0, y: 0 }}
        animate={{ x: 0, y: 0 }}
        exit={{ x: 0, y: 0 }}
        className="fixed z-[9999] w-96 max-h-[80vh] bg-white rounded-lg shadow-xl border border-green-200 overflow-hidden select-none"
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
      >
        {/* Header */}
        <div className="bg-green-500 text-white p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5" />
            <h3 className="font-semibold">{title}</h3>
            <span className="bg-white text-green-500 px-2 py-1 rounded-full text-xs font-bold">0</span>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={handleCollapseClick} className="p-1 hover:bg-green-600 rounded transition-colors" title={isCollapsed ? "Expand" : "Collapse"}>
              {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
            <button onClick={handleMinimizeClick} className="p-1 hover:bg-green-600 rounded transition-colors" title="Minimize">
              <Minus className="w-4 h-4" />
            </button>
          </div>
        </div>

      <AnimatePresence>
        {!isCollapsed && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="p-4 max-h-96 overflow-y-auto">
                <div className="text-center text-green-600 py-8">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p className="text-lg font-medium">No Validation Errors</p>
                  <p className="text-sm text-green-500 mt-2">All fields are properly filled</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={sidebarRef}
      initial={{ x: 0, y: 0 }}
      animate={{ x: 0, y: 0 }}
      exit={{ x: 0, y: 0 }}
      className="fixed z-[9999] w-96 max-h-[80vh] bg-white rounded-lg shadow-xl border border-red-200 overflow-hidden select-none"
      style={{ left: position.x, top: position.y }}
      onMouseDown={handleMouseDown}
    >
      {/* Header - Draggable area */}
      <div className="bg-red-500 text-white p-4 flex items-center justify-between cursor-move">
                <div className="flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <h3 className="font-semibold">{title}</h3>
          <span className="bg-white text-red-500 px-2 py-1 rounded-full text-xs font-bold">{errorCount}</span>
                </div>
                <div className="flex items-center space-x-2">
          <button onClick={handleCollapseClick} className="p-1 hover:bg-red-600 rounded transition-colors" title={isCollapsed ? "Expand" : "Collapse"}>
            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </button>
          <button onClick={handleMinimizeClick} className="p-1 hover:bg-red-600 rounded transition-colors" title="Minimize">
            <Minus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="space-y-3">
                {Object.entries(errorObject).map(([fieldKey, errorMessage]) => (
                  <div key={fieldKey} className="p-3 bg-red-50 border border-red-200 rounded-lg cursor-pointer hover:bg-red-100 transition-colors" onClick={() => handleFieldClick(fieldKey)}>
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                        <div className="text-sm text-red-600">{errorMessage}</div>
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ValidationErrorSidebar;
